import React, { useEffect } from "react";
import { Box, Container, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { scroller } from "react-scroll";
import MicNoneRoundedIcon from "@mui/icons-material/MicNoneRounded";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ReplayIcon from "@mui/icons-material/Replay";

const INK = "#0f1121";
const INK_SOFT = "rgba(15, 17, 33, 0.62)";
const INK_MUTED = "rgba(15, 17, 33, 0.55)";
const INK_FAINT = "rgba(15, 17, 33, 0.42)";
const INK_HAIRLINE = "rgba(15, 17, 33, 0.08)";
const ACCENT = "#3854e8";
const ACCENT_SOFT = "rgba(56, 84, 232, 0.12)";
const SURFACE = "#ffffff";
const SURFACE_MUTED = "rgba(15, 17, 33, 0.025)";
const CANVAS = "#f6f6f4";
const POSITIVE = "#1f8a5a";
const POSITIVE_SOFT = "rgba(31, 138, 90, 0.12)";
const WARN = "#c25a1f";
const WARN_SOFT = "rgba(194, 90, 31, 0.12)";
const REC = "#d4264a";

const SANS = "'sohne-var', 'Helvetica Neue', 'Arial', sans-serif";
const MONO =
  "'JetBrains Mono', 'Söhne Mono', ui-monospace, SFMono-Regular, Menlo, monospace";

const visualCardSx = {
  position: "relative",
  backgroundColor: SURFACE,
  borderRadius: "20px",
  border: `1px solid ${INK_HAIRLINE}`,
  boxShadow:
    "inset 0 1px 0 rgba(255, 255, 255, 0.6), 0 24px 60px -32px rgba(15, 17, 33, 0.18), 0 4px 10px -6px rgba(15, 17, 33, 0.06)",
  overflow: "hidden",
  textAlign: "left"
};

const eyebrowSx = {
  fontFamily: MONO,
  fontSize: "0.68rem",
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  color: INK_FAINT
};

const inkButtonSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.75,
  px: 1.75,
  py: 0.95,
  borderRadius: "10px",
  backgroundColor: INK,
  color: "#fafaf7",
  fontFamily: SANS,
  fontSize: "0.88rem",
  fontWeight: 500,
  letterSpacing: "0.01em",
  boxShadow:
    "inset 0 1px 0 rgba(255, 255, 255, 0.08), 0 8px 18px -10px rgba(15, 17, 33, 0.45)"
};

const navPillSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.5,
  px: 1.25,
  py: 0.65,
  border: `1px solid ${INK_HAIRLINE}`,
  borderRadius: "999px",
  backgroundColor: SURFACE,
  color: INK,
  fontFamily: SANS,
  fontSize: "0.82rem",
  fontWeight: 500
};

const StageChip = ({ num, label, active }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.75,
      py: 0.55,
      px: 1.1,
      borderRadius: "999px",
      border: `1px solid ${active ? INK : INK_HAIRLINE}`,
      backgroundColor: active ? INK : "transparent",
      color: active ? "#fafaf7" : INK_FAINT
    }}
  >
    <Typography
      sx={{
        fontFamily: MONO,
        fontSize: "0.66rem",
        fontWeight: 500,
        letterSpacing: "0.12em",
        fontVariantNumeric: "tabular-nums"
      }}
    >
      {num}
    </Typography>
    <Typography
      sx={{
        fontFamily: SANS,
        fontSize: "0.74rem",
        fontWeight: 500,
        letterSpacing: "0.04em",
        textTransform: "uppercase"
      }}
    >
      {label}
    </Typography>
  </Box>
);

const BlinkingCaret = ({ height = 18, color = ACCENT, ml = 0 }) => (
  <Box
    component={motion.span}
    animate={{ opacity: [1, 0.15, 1] }}
    transition={{ duration: 1.1, repeat: Infinity, ease: "easeInOut" }}
    sx={{
      display: "inline-block",
      width: "1.5px",
      height,
      backgroundColor: color,
      verticalAlign: "text-bottom",
      ml,
      borderRadius: "1px"
    }}
  />
);

const RoleVisual = () => (
  <Box sx={{ ...visualCardSx, p: 3 }}>
    <Typography sx={{ ...eyebrowSx, mb: 1.5 }}>Role</Typography>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        py: 1.5,
        px: 1.75,
        borderRadius: "12px",
        border: `1px solid ${ACCENT}`,
        boxShadow: `0 0 0 3px ${ACCENT_SOFT}`,
        backgroundColor: SURFACE,
        mb: 2
      }}
    >
      <Typography
        sx={{
          fontFamily: SANS,
          fontSize: "1.05rem",
          fontWeight: 500,
          color: INK,
          lineHeight: 1
        }}
      >
        Senior Product Designer
      </Typography>
      <BlinkingCaret height={18} />
    </Box>
    <Typography
      sx={{
        fontFamily: SANS,
        fontSize: "0.88rem",
        lineHeight: 1.5,
        color: INK_FAINT,
        mb: 2.5
      }}
    >
      Tailored to the role — behavioral and technical, ten questions per set.
    </Typography>
    <Box sx={inkButtonSx}>
      Generate questions
      <ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />
    </Box>
  </Box>
);

const RecordVisual = () => (
  <Box sx={{ ...visualCardSx, p: 3 }}>
    <Box sx={{ display: "flex", gap: 1, mb: 2.5, flexWrap: "wrap" }}>
      <StageChip num="01" label="Record" active />
      <StageChip num="02" label="Transcribe" />
      <StageChip num="03" label="Feedback" />
    </Box>
    <Box
      sx={{
        border: `1px solid ${INK_HAIRLINE}`,
        backgroundColor: SURFACE_MUTED,
        borderRadius: "16px",
        p: 2.5,
        display: "flex",
        alignItems: "center",
        gap: 2
      }}
    >
      <Box sx={{ position: "relative", width: 60, height: 60, flexShrink: 0 }}>
        <Box
          aria-hidden
          component={motion.span}
          animate={{ scale: [0.9, 1.4], opacity: [0.85, 0] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeOut" }}
          sx={{
            position: "absolute",
            inset: -4,
            borderRadius: "50%",
            border: `2px solid ${REC}`
          }}
        />
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            width: 60,
            height: 60,
            borderRadius: "50%",
            backgroundColor: REC,
            color: "#fafaf7",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 12px 28px -10px rgba(212, 38, 74, 0.5)"
          }}
        >
          <MicNoneRoundedIcon sx={{ fontSize: 26 }} />
        </Box>
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontFamily: SANS,
            fontSize: "1rem",
            fontWeight: 500,
            color: INK,
            lineHeight: 1.3
          }}
        >
          Recording…
        </Typography>
        <Typography
          sx={{
            fontFamily: MONO,
            fontSize: "0.78rem",
            color: INK_FAINT,
            fontVariantNumeric: "tabular-nums",
            mt: 0.5,
            letterSpacing: "0.06em"
          }}
        >
          00:42 — Re-tap mic to redo
        </Typography>
      </Box>
    </Box>
  </Box>
);

const TranscriptVisual = () => (
  <Box sx={{ ...visualCardSx, p: 3 }}>
    <Typography sx={{ ...eyebrowSx, mb: 1.25 }}>
      Transcript — edit before sending
    </Typography>
    <Box
      sx={{
        border: `1px solid ${INK_HAIRLINE}`,
        borderRadius: "12px",
        p: 2,
        backgroundColor: SURFACE,
        mb: 2.25,
        boxShadow: "inset 0 1px 0 rgba(15, 17, 33, 0.02)"
      }}
    >
      <Typography
        sx={{
          fontFamily: SANS,
          fontSize: "0.95rem",
          lineHeight: 1.7,
          color: INK
        }}
      >
        I led a checkout migration that cut p95 latency by 38% across the
        funnel. The constraint was a fixed two-week window before launch
        <BlinkingCaret height={"0.95rem"} ml={0.25} />
        , so I scoped down to the read-path first.
      </Typography>
    </Box>
    <Box sx={inkButtonSx}>
      <AutoAwesomeOutlinedIcon sx={{ fontSize: 16 }} />
      Get written feedback
    </Box>
  </Box>
);

const FeedbackVisual = () => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}>
    <Box sx={visualCardSx}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 1.6,
          px: 2.5,
          borderBottom: `1px solid ${INK_HAIRLINE}`,
          backgroundColor: "rgba(15, 17, 33, 0.02)"
        }}
      >
        <Typography
          sx={{
            fontFamily: SANS,
            fontSize: "0.92rem",
            fontWeight: 600,
            color: INK
          }}
        >
          Written feedback
        </Typography>
        <Typography sx={eyebrowSx}>AI · 2 sec ago</Typography>
      </Box>
      <Box sx={{ px: 2.5, py: 0.5 }}>
        {[
          {
            num: "01",
            label: "Strengths",
            accent: POSITIVE,
            accentSoft: POSITIVE_SOFT,
            count: 2,
            item: "Specific metric anchored the impact (cut p95 by 38%)."
          },
          {
            num: "02",
            label: "Watch-outs",
            accent: WARN,
            accentSoft: WARN_SOFT,
            count: 1,
            item:
              "You glossed over the team — interviewers will ask who did what."
          },
          {
            num: "03",
            label: "Next time",
            accent: ACCENT,
            accentSoft: ACCENT_SOFT,
            count: 2,
            item:
              "Name the trade-off you rejected before landing on the design."
          }
        ].map(s => (
          <Box
            key={s.label}
            sx={{
              py: 1.75,
              pl: 2,
              borderLeft: `2px solid ${s.accent}`
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 0.75
              }}
            >
              <Typography
                sx={{
                  fontFamily: MONO,
                  fontSize: "0.66rem",
                  fontWeight: 500,
                  letterSpacing: "0.16em",
                  color: INK_FAINT,
                  fontVariantNumeric: "tabular-nums"
                }}
              >
                {s.num}
              </Typography>
              <Typography
                sx={{
                  fontFamily: SANS,
                  fontSize: "0.74rem",
                  fontWeight: 600,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: INK
                }}
              >
                {s.label}
              </Typography>
              <Box
                sx={{
                  px: 0.85,
                  py: 0.15,
                  borderRadius: "999px",
                  backgroundColor: s.accentSoft,
                  color: s.accent,
                  fontFamily: MONO,
                  fontSize: "0.62rem",
                  fontWeight: 500,
                  fontVariantNumeric: "tabular-nums"
                }}
              >
                {s.count}
              </Box>
            </Box>
            <Typography
              sx={{
                fontFamily: SANS,
                fontSize: "0.88rem",
                lineHeight: 1.55,
                color: INK_SOFT
              }}
            >
              {s.item}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1
      }}
    >
      <Box sx={navPillSx}>
        <NavigateBeforeIcon sx={{ fontSize: 16 }} />
        Previous
      </Box>
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          color: INK_FAINT,
          fontFamily: SANS,
          fontSize: "0.82rem",
          fontWeight: 500
        }}
      >
        <ReplayIcon sx={{ fontSize: 14 }} />
        Retry this question
      </Box>
      <Box sx={navPillSx}>
        Next
        <NavigateNextIcon sx={{ fontSize: 16 }} />
      </Box>
    </Box>
  </Box>
);

const steps = [
  {
    title: "Tell us the role.",
    body:
      "Drop in a job title or paste the posting. We tailor a 10-question set — behavioral and technical — to match.",
    Visual: RoleVisual
  },
  {
    title: "Record each answer aloud.",
    body:
      "One question at a time, in your own voice. Tap the mic, talk it through, tap again to stop. We handle the audio.",
    Visual: RecordVisual
  },
  {
    title: "Edit the transcript.",
    body:
      "Speech-to-text isn't perfect. Tweak a word, fix a name, sharpen a sentence — then send it for review.",
    Visual: TranscriptVisual
  },
  {
    title: "Read the feedback. Keep going.",
    body:
      "Strengths, watch-outs, and what to say next time — in seconds. Save the question, retry it, or move to the next.",
    Visual: FeedbackVisual
  }
];

const stepTransition = { type: "spring", stiffness: 110, damping: 22 };
const fadeUp = { initial: { opacity: 0, y: 18 }, whileInView: { opacity: 1, y: 0 } };
const viewportConfig = { once: true, margin: "-60px" };

const HowItWorks = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.state && location.state.scrollTo) {
      scroller.scrollTo(location.state.scrollTo, {
        duration: 500,
        smooth: true,
        offset: -70
      });
    }
  }, [location]);

  return (
    <Box
      id="how-it-works"
      sx={{
        position: "relative",
        backgroundColor: CANVAS,
        py: { xs: 8, md: 14 },
        px: { xs: 2, sm: 3 },
        overflow: "hidden",
        textAlign: "left"
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(ellipse 70% 55% at 78% -8%, rgba(228, 222, 207, 0.55), transparent 65%), radial-gradient(ellipse 55% 45% at 16% 6%, rgba(56, 84, 232, 0.07), transparent 72%), radial-gradient(ellipse 90% 40% at 50% -2%, rgba(228, 222, 207, 0.32), transparent 78%), radial-gradient(ellipse 40% 28% at 32% 22%, rgba(15, 17, 33, 0.03), transparent 75%)",
          maskImage:
            "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.85) 22%, rgba(0,0,0,0.4) 42%, rgba(0,0,0,0) 60%)",
          WebkitMaskImage:
            "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.85) 22%, rgba(0,0,0,0.4) 42%, rgba(0,0,0,0) 60%)",
          pointerEvents: "none",
          zIndex: 0
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(rgba(15, 17, 33, 0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(15, 17, 33, 0.035) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage:
            "radial-gradient(ellipse at 50% 0%, rgba(0,0,0,0.4), transparent 78%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at 50% 0%, rgba(0,0,0,0.4), transparent 78%)",
          pointerEvents: "none",
          zIndex: 0
        }}
      />

      <Container
        maxWidth="lg"
        sx={{ position: "relative", zIndex: 1, textAlign: "left" }}
      >
        <Box
          component={motion.div}
          {...fadeUp}
          viewport={viewportConfig}
          transition={stepTransition}
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "minmax(0, 7fr) minmax(0, 5fr)"
            },
            gap: { xs: 3, md: 6 },
            alignItems: "end",
            mb: { xs: 6, md: 10 }
          }}
        >
          <Box sx={{ textAlign: "left" }}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                mb: 2.5
              }}
            >
              <Box
                component={motion.span}
                animate={{ opacity: [0.55, 1, 0.55] }}
                transition={{
                  duration: 2.4,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  backgroundColor: ACCENT
                }}
              />
              <Typography
                sx={{
                  fontFamily: MONO,
                  fontSize: "0.7rem",
                  fontWeight: 500,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: INK_MUTED
                }}
              >
                How it works
              </Typography>
            </Box>
            <Typography
              component="h2"
              sx={{
                fontFamily: SANS,
                fontWeight: 600,
                fontSize: { xs: "2.1rem", sm: "2.6rem", md: "3rem" },
                lineHeight: 1.02,
                letterSpacing: "-0.03em",
                color: INK,
                m: 0,
                mb: 2,
                maxWidth: "16ch",
                textAlign: "left"
              }}
            >
              From draft to delivery, in four moves.
            </Typography>
            <Typography
              sx={{
                fontFamily: SANS,
                fontSize: { xs: "1rem", md: "1.08rem" },
                lineHeight: 1.55,
                color: INK_SOFT,
                m: 0,
                maxWidth: "52ch",
                textAlign: "left"
              }}
            >
              You talk, you read, you adjust. Run the loop until the interview
              feels like a draft you've already written.
            </Typography>
          </Box>
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 1,
              pb: 0.5
            }}
          >
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
              <Typography
                sx={{
                  fontFamily: MONO,
                  fontSize: "3.4rem",
                  fontWeight: 500,
                  letterSpacing: "-0.04em",
                  color: INK,
                  fontVariantNumeric: "tabular-nums",
                  lineHeight: 0.95
                }}
              >
                ~6
              </Typography>
              <Typography
                sx={{
                  fontFamily: SANS,
                  fontSize: "0.95rem",
                  color: INK_SOFT
                }}
              >
                min per loop
              </Typography>
            </Box>
            <Typography
              sx={{
                fontFamily: MONO,
                fontSize: "0.66rem",
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: INK_FAINT,
                maxWidth: "22ch",
                textAlign: "right"
              }}
            >
              Ten of these and you're rehearsed.
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {steps.map((step, index) => {
            const visualOnRight = index % 2 === 0;
            return (
              <Box
                key={step.title}
                component={motion.div}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={viewportConfig}
                transition={stepTransition}
                sx={{
                  borderTop: `1px solid ${INK_HAIRLINE}`,
                  pt: { xs: 4, md: 6 },
                  pb: { xs: 4, md: 8 },
                  textAlign: "left"
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    mb: { xs: 3, md: 4 }
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: MONO,
                      fontSize: "0.74rem",
                      fontWeight: 500,
                      letterSpacing: "0.18em",
                      textTransform: "uppercase",
                      color: ACCENT,
                      fontVariantNumeric: "tabular-nums",
                      whiteSpace: "nowrap"
                    }}
                  >
                    {`Step ${String(index + 1).padStart(2, "0")} / 04`}
                  </Typography>
                  <Box
                    sx={{
                      flex: 1,
                      height: "1px",
                      backgroundColor: INK_HAIRLINE
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      md: visualOnRight
                        ? "minmax(0, 5fr) minmax(0, 7fr)"
                        : "minmax(0, 7fr) minmax(0, 5fr)"
                    },
                    gap: { xs: 4, md: 7 },
                    alignItems: "center"
                  }}
                >
                  <Box
                    sx={{
                      order: { xs: 2, md: visualOnRight ? 1 : 2 },
                      textAlign: "left"
                    }}
                  >
                    <Typography
                      component="h3"
                      sx={{
                        fontFamily: SANS,
                        fontWeight: 600,
                        fontSize: { xs: "1.6rem", md: "2rem" },
                        lineHeight: 1.08,
                        letterSpacing: "-0.025em",
                        color: INK,
                        m: 0,
                        mb: 2,
                        textAlign: "left"
                      }}
                    >
                      {step.title}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: SANS,
                        fontSize: { xs: "1rem", md: "1.06rem" },
                        lineHeight: 1.6,
                        color: INK_SOFT,
                        m: 0,
                        maxWidth: "44ch",
                        textAlign: "left"
                      }}
                    >
                      {step.body}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      order: { xs: 1, md: visualOnRight ? 2 : 1 }
                    }}
                  >
                    <step.Visual />
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Container>
    </Box>
  );
};

export default HowItWorks;
